import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Modal, Input, Button, Spin } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'context/AppContext';
import { Campaign, Enrollment } from 'models/CommonType';
import { useTeamsPaginated } from 'services/TeamService';
import TeamRow from './TeamRow';
import TeamCreationModal from './TeamCreationModal';
import TeamMemberDetailsModal from './TeamMemberDetailsModal';
import { Team } from 'models/CommonType';
import './TeamListModal.css';

interface TeamListModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign;
  authKey: string;
  enrollment?: Enrollment;
}

const TeamListModal: React.FC<TeamListModalProps> = ({
  isOpen,
  onClose,
  campaign,
  authKey,
  enrollment
}) => {
  const { t } = useTranslation();
  const { commonData } = useContext(AppContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTeamDetailsModalOpen, setIsTeamDetailsModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const observerRef = useRef(null);

  const {
    teams,
    totalCount,
    hasNextPage,
    isLoading,
    setSearchText,
    loadNextPage,
    refreshTeams,
  } = useTeamsPaginated(campaign.id, authKey, isOpen); // Only enable when modal is open

  // Teams data will automatically be fetched when isOpen becomes true
  // due to the enabled parameter in useTeamsPaginated

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchValue);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]); // Remove setSearchText from dependencies to avoid loop

  // Intersection Observer for pagination (following notifications pattern)
  const nextPageHandler = useCallback(() => {
    if (hasNextPage && !isLoading && isOpen) {
      loadNextPage();
    }
  }, [hasNextPage, isLoading, loadNextPage, isOpen]);

  useEffect(() => {
    if (!isOpen) return; // Don't setup observer when modal is closed
    
    let observerRefValue: Element | null = null;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          nextPageHandler();
        }
      },
      { threshold: 1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
      observerRefValue = observerRef.current;
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
    };
  }, [observerRef, nextPageHandler, isOpen]);

  const handleCreateTeamSuccess = () => {
    refreshTeams();
    setIsCreateModalOpen(false);
  };

  const handleClose = () => {
    setSearchValue('');
    setSearchText('');
    setSelectedTeam(null);
    setIsTeamDetailsModalOpen(false);
    onClose();
  };

  const handleTeamClick = (team: Team) => {
    setSelectedTeam(team);
    setIsTeamDetailsModalOpen(true);
  };

  const handleBackFromTeamDetails = () => {
    setIsTeamDetailsModalOpen(false);
    setSelectedTeam(null);
  };

  // Check if user is member of a team
  const isUserTeamMember = (teamMembers: any[]) => {
    if (!commonData.userDetails?.id) return false;
    return teamMembers.some(member => member.id === commonData.userDetails?.id);
  };

  const getTeamCount = () => {
    // Use totalCount from API if available, otherwise fall back to teams.length
    const count = totalCount > 0 ? totalCount : teams.length;
    if (count === 0) return t('#Teams.team_count_other', { count: 0 });
    if (count === 1) return t('#Teams.team_count_one', { count: 1 });
    return t('#Teams.team_count_other', { count });
  };

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        centered
        className="team-list-modal"
        width="90%"
        style={{ maxWidth: 600 }}
        closable={false}
      >
        <div className="team-list-content">
          {/* Header */}
          <div className="team-list-header">
            <h2 className="team-list-title">{t('#Teams.teams_title')}</h2>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              className="team-list-close"
            />
          </div>

          {/* Search Box */}
          <div className="team-search-container">
            <Input
              placeholder={t('#Teams.search_teams_placeholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              prefix={<SearchOutlined className="search-icon" />}
              className="team-search-input"
              allowClear
            />
          </div>

          {/* Header Info */}
          <div className="team-list-info">
            <div className="team-count">
              {getTeamCount()}
            </div>
            {!enrollment?.has_team && (
              <Button
                type="link"
                onClick={() => setIsCreateModalOpen(true)}
                className="create-team-link"
              >
                {t('#Teams.create_team_link')}
              </Button>
            )}
          </div>

          {/* Teams List */}
          <div className="team-list-scroll-container">
            {!isOpen ? (
              // Don't render anything if modal is closed
              null
            ) : isLoading && teams.length === 0 ? (
              <div className="team-list-loading">
                <Spin size="large" />
                <p>Loading teams...</p>
              </div>
            ) : teams.length === 0 ? (
              <div className="team-list-empty">
                <p>No teams found.</p>
                {!enrollment?.has_team && (
                  <Button
                    type="primary"
                    onClick={() => setIsCreateModalOpen(true)}
                    className="create-team-button"
                  >
                    {t('#Teams.create_team_link')}
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="team-list-items">
                  {teams.map((team, index) => (
                    <div 
                      key={`${team.id}-${index}`} 
                      className="team-list-item"
                      onClick={() => handleTeamClick(team)}
                    >
                      <TeamRow
                        team={team}
                        hasTeam={enrollment?.has_team || false}
                        shouldShowMiles={true}
                      />
                      {isUserTeamMember(team.members) && (
                        <div className="your-team-indicator">
                          <span className="your-team-badge">
                            {t('#Teams.your_team')}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Loading indicator for pagination */}
                {isLoading && (
                  <div className="team-list-loader">
                    <Spin />
                  </div>
                )}
                
                {/* End message when no more teams */}
                {!hasNextPage && teams.length > 0 && (
                  <div className="team-list-end">
                    <p>You've seen all teams!</p>
                  </div>
                )}
              </>
            )}
            
            {/* Intersection Observer target */}
            <div ref={observerRef}></div>
          </div>
        </div>
      </Modal>

      {/* Team Creation Modal */}
      <TeamCreationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        campaign={campaign}
        authKey={authKey}
        onSuccess={handleCreateTeamSuccess}
      />

      {/* Team Member Details Modal */}
      {selectedTeam && (
        <TeamMemberDetailsModal
          isOpen={isTeamDetailsModalOpen}
          onClose={handleClose}
          onBack={handleBackFromTeamDetails}
          team={selectedTeam}
          authKey={authKey}
          hasTeam={enrollment?.has_team}
        />
      )}
    </>
  );
};

export default TeamListModal;